'use client';

import { useEffect, useRef, useState } from 'react';
import { logger } from '@/core/logger';

export interface Video {
	url: string;
	placeholder: string;
}

// All available hero videos
const allVideos: Video[] = [
	{
		url: '/hero-video-1.mp4',
		placeholder:
			'The Indonesian girl turns her head to the camera, flowers gently sway...',
	},
	{
		url: '/hero-video-2.mp4',
		placeholder: 'The car driving on a road with a beautiful scenery...',
	},
	{
		url: '/hero-video-3.mp4',
		placeholder: 'The rider on e-bike, riding through Kuala Lumpur city...',
	},
	{
		url: '/hero-video-4.mp4',
		placeholder:
			'The Malay lady walks elegantly showing off her handbag...',
	},
	{
		url: '/hero-video-5.mp4',
		placeholder:
			'The girl applying lipstick, and then looks at the camera...',
	},
	{
		url: '/hero-video-7.mp4',
		placeholder: 'A scene of cat and dog looking at the camera...',
	},
	{
		url: '/hero-video-8.mp4',
		placeholder: 'A female anime character charging up her magic...',
	},
	{
		url: '/hero-video-9.mp4',
		placeholder:
			'As the surrounding collapsing around them, they rushing to escape...',
	},
	{
		url: '/hero-video-10.mp4',
		placeholder: 'A futuristic soldier charging to attack...',
	},
	{
		url: '/hero-video-11.mp4',
		placeholder: 'A beautiful white tiger charging to attack...',
	},
	{
		url: '/hero-video-12.mp4',
		placeholder:
			'A girl wearing tech-wear on a skateboard on the street...',
	},
	{
		url: '/hero-video-13.mp4',
		placeholder: 'A bunch of friends cheering their glasses up...',
	},
	{
		url: '/hero-video-14.mp4',
		placeholder: 'A man enters a haunted house...',
	},
	{
		url: '/hero-video-15.mp4',
		placeholder:
			'The lady drinking coffee, suddenly a meteor drops from the sky...',
	},
	{
		url: '/hero-video-16.mp4',
		placeholder:
			'A filmaker walk foward with his camera in black and white...',
	},
	{
		url: '/hero-video-17.mp4',
		placeholder:
			'A film director sits in his chair thinking about the next scene...',
	},
	{
		url: '/hero-video-18.mp4',
		placeholder:
			'A person interacting with floating holographic UI screens...',
	},
	{
		url: '/hero-video-19.mp4',
		placeholder:
			'Close-up slow-motion shot of a barista pouring latte art in a cosy café...',
	},
	{
		url: '/hero-video-20.mp4',
		placeholder:
			'A slow forward-moving shot through a mist-filled pine forest at sunrise...',
	},
	{
		url: '/hero-video-21.mp4',
		placeholder:
			'A black luxury sports car racing through a tunnel at night...',
	},
	{
		url: '/hero-video-22.mp4',
		placeholder:
			'A static camera in a busy pedestrian crossing in Shibuya...',
	},
	{
		url: '/hero-video-23.mp4',
		placeholder:
			'Massive thunderstorm forming over a reddish desert landscape...',
	},
	{
		url: '/hero-video-24.mp4',
		placeholder:
			'A model walking in a minimalist white studio with dramatic overhead lighting...',
	},
	{
		url: '/hero-video-25.mp4',
		placeholder:
			'Drone fly-over of waves crashing into sharp seaside cliffs at golden sunrise...',
	},
	{
		url: '/hero-video-26.mp4',
		placeholder:
			'Clouds rushing over a financial-district skyline with glass skyscrapers..',
	},
	{
		url: '/hero-video-27.mp4',
		placeholder:
			'A lone figure crossing a neon-lit street in futuristic Tokyo under the rain...',
	},
];

// Shuffle array function
function shuffleArray<T>(array: T[]): T[] {
	const shuffled = [...array];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}

// Pick N random videos from array
function pickRandomVideos<T>(array: T[], count: number): T[] {
	const shuffled = shuffleArray(array);
	return shuffled.slice(0, count);
}

interface VideoCarouselProps {
	videos?: Video[];
	onPlaceholderChange?: (placeholder: string) => void;
	contained?: boolean; // When true, video is contained in parent instead of fullscreen
	objectFit?: 'cover' | 'contain'; // Control how video fits in container
}

export default function VideoCarousel({
	videos,
	onPlaceholderChange,
	contained = false,
	objectFit = 'cover',
}: VideoCarouselProps) {
	// Use unshuffled videos initially to avoid hydration mismatch
	const sourceVideos = videos || allVideos;
	const [shuffledVideos, setShuffledVideos] = useState(sourceVideos);
	const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
	const videoRefs = useRef<Map<number, HTMLVideoElement | null>>(new Map());
	const shouldBePlaying = useRef(false); // Track intended playback state
	const hasShuffled = useRef(false);

	// Only render current video and 2 ahead/behind for performance
	const renderRange = 2;
	const getVisibleVideoIndices = () => {
		const indices = [];
		for (let i = -renderRange; i <= renderRange; i++) {
			const index =
				(currentVideoIndex + i + shuffledVideos.length) %
				shuffledVideos.length;
			indices.push(index);
		}
		return indices;
	};

	// Pick 5 random videos after mount (client-side only) to avoid hydration mismatch
	useEffect(() => {
		if (!hasShuffled.current) {
			hasShuffled.current = true;
			// Pick only 5 random videos from the available videos
			const selectedVideos = pickRandomVideos(sourceVideos, 5);
			setShuffledVideos(selectedVideos);
		}
	}, [sourceVideos]);

	// Notify parent of placeholder change whenever video index or array changes
	useEffect(() => {
		if (onPlaceholderChange && shuffledVideos.length > 0) {
			onPlaceholderChange(shuffledVideos[currentVideoIndex].placeholder);
		}
	}, [currentVideoIndex, shuffledVideos, onPlaceholderChange]);

	// Preload only rendered videos to reduce initial load
	useEffect(() => {
		const visibleIndices = getVisibleVideoIndices();

		visibleIndices.forEach((index) => {
			const video = videoRefs.current.get(index);

			if (video && !video.getAttribute('data-loaded')) {
				video.load();
				video.setAttribute('data-loaded', 'true');
				// Set webkit-specific attributes for Safari compatibility
				video.setAttribute('webkit-playsinline', 'true');
				video.setAttribute('x-webkit-airplay', 'allow');
			}
		});
	}, [currentVideoIndex, shuffledVideos]);

	// Play current video when index changes
	useEffect(() => {
		const currentVideo = videoRefs.current.get(currentVideoIndex);
		if (!currentVideo) return;

		// Pause all other videos
		videoRefs.current.forEach((video, index) => {
			if (video && index !== currentVideoIndex) {
				video.pause();
				video.currentTime = 0;
			}
		});

		// Play current video
		const playVideo = async () => {
			try {
				// Wait a bit to ensure the video element is ready
				await new Promise((resolve) => setTimeout(resolve, 100));
				currentVideo.currentTime = 0;
				shouldBePlaying.current = true;
				await currentVideo.play();
			} catch (error) {
				logger.warn('Video play failed', error);
				shouldBePlaying.current = false;
			}
		};

		playVideo();
	}, [currentVideoIndex, shuffledVideos]);

	// Handle Page Visibility API and Focus events for Safari
	// Safari pauses videos aggressively when tab is hidden or browser is minimized
	useEffect(() => {
		const attemptResume = () => {
			if (!shouldBePlaying.current) return;

			const currentVideo = videoRefs.current.get(currentVideoIndex);
			if (!currentVideo) return;

			if (currentVideo.paused) {
				// Small delay to ensure Safari is ready
				setTimeout(() => {
					currentVideo.play().catch((error) => {
						logger.warn('Failed to resume video playback', error);
					});
				}, 100);
			}
		};

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				attemptResume();
			}
		};

		const handleFocus = () => {
			attemptResume();
		};

		// Listen to multiple events for better Safari compatibility
		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('focus', handleFocus);

		return () => {
			document.removeEventListener(
				'visibilitychange',
				handleVisibilityChange
			);
			window.removeEventListener('focus', handleFocus);
		};
	}, [currentVideoIndex]);

	const handleVideoEnd = () => {
		setCurrentVideoIndex((prev) => (prev + 1) % shuffledVideos.length);
	};

	return (
		<>
			{/* Only render videos in the visible range */}
			{getVisibleVideoIndices().map((index) => {
				const video = shuffledVideos[index];
				return (
					<video
						key={`${video.url}-${index}`}
						ref={(el) => {
							videoRefs.current.set(index, el);
						}}
						muted
						playsInline
						preload="auto"
						onEnded={
							index === currentVideoIndex
								? handleVideoEnd
								: undefined
						}
						onPause={(e) => {
							// Track if video was paused by Safari (not by us)
							const target = e.currentTarget;
							if (
								index === currentVideoIndex &&
								shouldBePlaying.current
							) {
								// Safari paused it - try to resume after a short delay
								setTimeout(() => {
									if (
										shouldBePlaying.current &&
										target.paused
									) {
										target.play().catch(() => {
											// Silently fail - will retry on visibility change
										});
									}
								}, 50);
							}
						}}
						style={{
							position: contained ? 'absolute' : 'fixed',
							top: 0,
							left: 0,
							width: contained ? '100%' : '100vw',
							height: contained ? '100%' : '100vh',
							objectFit: objectFit,
							zIndex: contained ? 1 : -10,
							opacity: index === currentVideoIndex ? 1 : 0,
							pointerEvents: 'none',
							transition: 'opacity 0.5s ease-in-out',
						}}
					>
						<source src={video.url} type="video/mp4" />
					</video>
				);
			})}

			{/* Overlay for text readability */}
			<div
				style={{
					position: contained ? 'absolute' : 'fixed',
					top: 0,
					left: 0,
					width: contained ? '100%' : '100vw',
					height: contained ? '100%' : '100vh',
					background:
						'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.1), rgba(0,0,0,0.4))',
					zIndex: contained ? 2 : -9,
					pointerEvents: 'none',
				}}
			/>
		</>
	);
}
